import { Head, usePage } from '@inertiajs/react'
import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import styles from './Home.module.css'
import apiClient from '../utils/api'
import Calendar from '../components/Calendar'
import ManageSection from '../components/ManageSection'
import ReportsSection from '../components/ReportsSection'
import TrackerSection from '../components/TrackerSection'
import Preferences from '../components/Preferences'
import WorkspaceModal from '../components/modals/WorkspaceModal'
import CommandPalette from '../components/CommandPalette'
import StagingBanner from '../components/StagingBanner'
import TimezonePrompt from '../components/TimezonePrompt'
import useCommandK from '../hooks/useCommandK'
import { detectTimezone, isValidTimezone, timezonesEqual, normalizeTimezone } from '../utils/timezoneUtils'
import { initFeatureTiers } from '../utils/featureGate'

export default function Home({ app_name, version, user, current_workspace, available_workspaces, api_token, environment, revision, gitlab_project_url }) {
    // Initialize feature tiers cache from Inertia shared data (once, at top level)
    const { feature_tiers } = usePage().props
    initFeatureTiers(feature_tiers)
    // Get initial section from URL or default to tracker
    const getInitialSection = () => {
        const path = window.location.pathname
        if (path.includes('/calendar')) return 'calendar'
        if (path.includes('/reports')) return 'reports'
        if (path.includes('/manage')) return 'manage'
        if (path.includes('/tracker')) return 'tracker'
        return 'tracker'
    }

    // Get initial manage sub-section from URL
    const getInitialManageSubSection = () => {
        const path = window.location.pathname
        if (path === '/manage/clients') return 'clients'
        if (path === '/manage/projects') return 'projects'
        if (path === '/manage/groups' || path.startsWith('/manage/groups/')) return 'groups'
        if (path === '/manage/members' || path.startsWith('/manage/members/')) return 'members'
        if (path === '/manage/import') return 'import'
        return 'projects'
    }

    // Get member ID from URL if present
    const getInitialSelectedMemberId = () => {
        const path = window.location.pathname
        const match = path.match(/^\/manage\/members\/(.+)$/)
        return match ? match[1] : null
    }

    // Get group ID from URL if present
    const getInitialSelectedGroupId = () => {
        const path = window.location.pathname
        const match = path.match(/^\/manage\/groups\/(.+)$/)
        return match ? match[1] : null
    }

    // Get project ID from URL if present
    const getInitialSelectedProjectId = () => {
        const path = window.location.pathname
        const match = path.match(/^\/manage\/projects\/(.+)$/)
        return match ? match[1] : null
    }


    // Get initial reports view mode from URL
    const getInitialReportsViewMode = () => {
        const path = window.location.pathname
        if (path === '/reports/detailed') return 'detailed'
        return 'summary'
    }

    // Get initial tracker sub-section from URL
    const getInitialTrackerSubSection = () => {
        const path = window.location.pathname
        if (path === '/tracker/timesheet') return 'timesheet'
        if (path === '/tracker/timelog') return 'timelog'
        return 'timelog'
    }

    const [activeSection, setActiveSection] = useState(getInitialSection())
    const [showUserMenu, setShowUserMenu] = useState(false)
    const [showPreferences, setShowPreferences] = useState(false) // can be false, 'appearance', 'account', etc.
    const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false)
    const [showWorkspaceModal, setShowWorkspaceModal] = useState(false)
    const [showTimezonePrompt, setShowTimezonePrompt] = useState(false)
    const [detectedTimezone, setDetectedTimezone] = useState(null)
    const [availableProjects, setAvailableProjects] = useState([])
    const [availableTasks, setAvailableTasks] = useState([])
    const [manageSubSection, setManageSubSection] = useState(getInitialManageSubSection()) // 'clients', 'projects', 'groups', 'members'
    const [selectedMemberId, setSelectedMemberId] = useState(getInitialSelectedMemberId())
    const [selectedGroupId, setSelectedGroupId] = useState(getInitialSelectedGroupId())
    const [selectedProjectId, setSelectedProjectId] = useState(getInitialSelectedProjectId())
    const [reportsSubSection, setReportsSubSection] = useState('dashboard') // 'dashboard', 'automated-reports'
    const [reportsViewMode, setReportsViewMode] = useState(getInitialReportsViewMode()) // 'summary', 'detailed'
    const [trackerSubSection, setTrackerSubSection] = useState(getInitialTrackerSubSection()) // 'timelog', 'timesheet'
    const [currentTheme, setCurrentTheme] = useState(() => {
        // Check localStorage for saved theme, default to dark
        if (typeof window !== 'undefined') {
            return localStorage.getItem('theme') || 'dark'
        }
        return 'dark'
    }) // 'light', 'dark', 'nightowl', 'pastel', 'gamer'
    const menuRef = useRef(null)
    const workspaceMenuRef = useRef(null)

    // Command K functionality
    const { isOpen: isCommandPaletteOpen, closePalette: closeCommandPalette } = useCommandK()

    // Worklog refresh functionality 
    const [worklogRefreshCallback, setWorklogRefreshCallback] = useState(null)

    const handleCommandSuccess = (action, result) => {
        // Refresh worklog data for timer/worklog operations
        if (['timer_started', 'timer_stopped', 'worklog_created'].includes(action)) {
            if (worklogRefreshCallback) {
                const refreshFn = worklogRefreshCallback()
                if (refreshFn) {
                    refreshFn()
                }
            }
        }
    }

    const handleUpdateTimezone = async () => {
        try {
            apiClient.setAuthToken(api_token)
            // Normalize timezone to handle aliases (e.g., Asia/Calcutta -> Asia/Kolkata)
            const normalizedTimezone = normalizeTimezone(detectedTimezone)
            const response = await apiClient.updateTimezone(normalizedTimezone, true)

            // Update user object with new timezone
            router.reload({ only: ['user'] })
            setShowTimezonePrompt(false)
        } catch (error) {
            console.error('Failed to update timezone:', error)
            throw error
        }
    }

    const handleDismissTimezone = () => {
        localStorage.setItem(`timezone_prompt_dismissed_${detectedTimezone}`, new Date().toISOString())
        setShowTimezonePrompt(false)
    }

    // Apply theme to document root on mount and theme change
    useEffect(() => {
        const applyTheme = (theme) => {
            const root = document.documentElement

            // Remove all theme attributes
            root.removeAttribute('data-theme')

            // Apply the selected theme
            if (theme === 'dark') {
                root.setAttribute('data-theme', 'dark')
            } else if (theme === 'nightowl') {
                root.setAttribute('data-theme', 'nightowl')
            } else if (theme === 'pastel') {
                root.setAttribute('data-theme', 'pastel')
            } else if (theme === 'gamer') {
                root.setAttribute('data-theme', 'gamer')
            }
        }

        applyTheme(currentTheme)
    }, [currentTheme])

    // Load projects and tasks data
    useEffect(() => {
        const loadProjectsAndTasks = async () => {
            if (!api_token) return

            try {
                apiClient.setAuthToken(api_token)
                const projectsData = await apiClient.getProjects()
                const projects = projectsData.projects || []

                // Filter to only include active projects
                const activeProjects = projects.filter(p => p.status === 'active')

                setAvailableProjects(activeProjects)

                // Load tasks for active projects only
                if (activeProjects.length > 0) {
                    const allTasks = await apiClient.getAllTasks(activeProjects)
                    setAvailableTasks(allTasks)
                } else {
                    setAvailableTasks([])
                }
            } catch (err) {
                console.error('Failed to load projects and tasks:', err)
                // Set empty arrays on error to prevent undefined behavior
                setAvailableProjects([])
                setAvailableTasks([])
            }
        }

        loadProjectsAndTasks()
    }, [api_token])

    // Check and prompt for timezone confirmation
    useEffect(() => {
        const checkTimezone = () => {
            const userTimezone = user?.timezone
            const confirmedAt = user?.timezone_confirmed_at
            const detectedTz = detectTimezone()

            // Validate detected timezone
            if (!isValidTimezone(detectedTz)) {
                return
            }

            // Don't prompt if user has confirmed timezone and it matches detected
            // Use timezonesEqual to handle aliases like Asia/Calcutta -> Asia/Kolkata
            if (confirmedAt && userTimezone && timezonesEqual(userTimezone, detectedTz)) {
                return
            }

            // Check if user dismissed this timezone recently
            const dismissedKey = `timezone_prompt_dismissed_${detectedTz}`
            const dismissedAt = localStorage.getItem(dismissedKey)
            if (dismissedAt) {
                const daysSinceDismiss = (Date.now() - new Date(dismissedAt)) / (1000 * 60 * 60 * 24)
                if (daysSinceDismiss < 7) {
                    return // Don't nag for 7 days
                }
            }

            // Show prompt
            setDetectedTimezone(detectedTz)
            setShowTimezonePrompt(true)
        }

        if (user) {
            checkTimezone()
        }
    }, [user])

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false)
            }
            if (workspaceMenuRef.current && !workspaceMenuRef.current.contains(event.target)) {
                setShowWorkspaceMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            setActiveSection(getInitialSection())
            setReportsViewMode(getInitialReportsViewMode())
            setTrackerSubSection(getInitialTrackerSubSection())
            setManageSubSection(getInitialManageSubSection())
            setSelectedMemberId(getInitialSelectedMemberId())
            setSelectedGroupId(getInitialSelectedGroupId())
        }

        window.addEventListener('popstate', handlePopState)
        return () => {
            window.removeEventListener('popstate', handlePopState)
        }
    }, [])

    const handleLogout = () => {
        router.delete('/logout', {
            onSuccess: () => router.visit('/login'),
            onError: (errors) => router.visit('/login')
        })
    }

    const handleWorkspaceSwitch = async (workspaceId) => {
        try {
            const response = await fetch(`/api/v1/workspaces/${workspaceId}/switch`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                }
            })

            if (response.ok) {
                // Reload the page to get updated workspace context
                window.location.reload()
            } else {
                console.error('Failed to switch workspace')
            }
        } catch (error) {
            console.error('Error switching workspace:', error)
        }
        setShowWorkspaceMenu(false)
    }

    const handleWorkspaceCreate = async (workspaceData) => {
        try {
            // Use FormData for file upload support
            const formData = new FormData()

            // Add all workspace fields
            Object.keys(workspaceData).forEach(key => {
                if (key === 'supported_currencies' && Array.isArray(workspaceData[key])) {
                    // Handle array fields
                    workspaceData[key].forEach(currency => {
                        formData.append(`workspace[supported_currencies][]`, currency)
                    })
                } else if (workspaceData[key] !== null && workspaceData[key] !== undefined) {
                    formData.append(`workspace[${key}]`, workspaceData[key])
                }
            })

            const response = await fetch('/api/v1/workspaces', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    // Don't set Content-Type for FormData - browser will set it automatically with boundary
                },
                body: formData
            })

            if (response.ok) {
                // Reload the page to get updated workspace context
                window.location.reload()
            } else {
                const error = await response.json()
                throw new Error(error.error || 'Failed to create workspace')
            }
        } catch (error) {
            console.error('Error creating workspace:', error)
            throw error
        }
    }

    const handleWorkspaceUpdate = async (workspaceId, workspaceData) => {
        try {
            // Use FormData for file upload support
            const formData = new FormData()

            // Add all workspace fields
            Object.keys(workspaceData).forEach(key => {
                if (key === 'supported_currencies' && Array.isArray(workspaceData[key])) {
                    // Handle array fields
                    workspaceData[key].forEach(currency => {
                        formData.append(`workspace[supported_currencies][]`, currency)
                    })
                } else if (workspaceData[key] !== null && workspaceData[key] !== undefined) {
                    formData.append(`workspace[${key}]`, workspaceData[key])
                }
            })

            const response = await fetch(`/api/v1/workspaces/${workspaceId}`, {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    // Don't set Content-Type for FormData - browser will set it automatically with boundary
                },
                body: formData
            })

            if (response.ok) {
                // Reload the page to get updated workspace context
                window.location.reload()
            } else {
                const error = await response.json()
                throw new Error(error.error || 'Failed to update workspace')
            }
        } catch (error) {
            console.error('Error updating workspace:', error)
            throw error
        }
    }

    const handleWorkspaceDelete = async (workspaceId) => {
        try {
            const response = await fetch(`/api/v1/workspaces/${workspaceId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                }
            })

            if (response.ok) {
                // Reload the page to get updated workspace context
                window.location.reload()
            } else {
                const error = await response.json()
                throw new Error(error.error || 'Failed to delete workspace')
            }
        } catch (error) {
            console.error('Error deleting workspace:', error)
            throw error
        }
    }

    const toggleUserMenu = () => {
        setShowUserMenu(!showUserMenu)
    }

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : ''
        const last = lastName ? lastName.charAt(0).toUpperCase() : ''
        return first + last
    }

    const handleSectionChange = (section) => {
        setActiveSection(section)

        // Update URL using browser history API
        const urls = {
            tracker: '/tracker/timelog',
            calendar: '/calendar',
            reports: '/reports',
            manage: '/manage'
        }

        const targetUrl = urls[section] || '/'

        // Update browser URL without full page reload
        if (window.location.pathname !== targetUrl) {
            window.history.pushState({ section }, '', targetUrl)
        }

        // Reset reports view mode when switching to reports
        if (section === 'reports') {
            setReportsViewMode('summary')
        }

        // Reset tracker sub-section when switching to tracker
        if (section === 'tracker') {
            setTrackerSubSection('timelog')
        }

        // Reset manage sub-section when switching to manage
        if (section === 'manage') {
            setManageSubSection('projects')
        }
    }

    const handleReportsViewModeChange = (viewMode) => {
        setReportsViewMode(viewMode)

        // Update URL based on view mode
        const targetUrl = viewMode === 'detailed' ? '/reports/detailed' : '/reports'

        // Update browser URL without full page reload
        if (window.location.pathname !== targetUrl) {
            window.history.pushState({ section: 'reports', viewMode }, '', targetUrl)
        }
    }

    const handleTrackerSubSectionChange = (subSection) => {
        setTrackerSubSection(subSection)

        // Update URL based on sub-section
        const targetUrl = `/tracker/${subSection}`

        // Update browser URL without full page reload
        if (window.location.pathname !== targetUrl) {
            window.history.pushState({ section: 'tracker', subSection }, '', targetUrl)
        }
    }

    const handleManageSubSectionChange = (subSection, memberId = null, groupId = null) => {
        setManageSubSection(subSection)

        if (subSection !== 'members') {
            setSelectedMemberId(null)
        }

        if (subSection !== 'groups') {
            setSelectedGroupId(null)
        }

        // Update URL based on sub-section and ID
        let targetUrl = `/manage/${subSection}`
        if (subSection === 'members' && memberId) {
            targetUrl = `/manage/members/${memberId}`
            setSelectedMemberId(memberId)
        } else if (subSection === 'groups' && groupId) {
            targetUrl = `/manage/groups/${groupId}`
            setSelectedGroupId(groupId)
        }

        // Update browser URL without full page reload
        if (window.location.pathname !== targetUrl) {
            window.history.pushState({ section: 'manage', subSection, memberId, groupId }, '', targetUrl)
        }
    }

    const renderContent = () => {
        // Redirect members away from admin-only sections
        if (!user?.is_admin && (activeSection === 'reports' || activeSection === 'manage')) {
            // Redirect to tracker section
            setActiveSection('tracker')
            window.history.pushState({ section: 'tracker' }, '', '/tracker/timelog')
            return (
                <div className={styles.content}>
                    <div className={styles.accessDenied}>
                        <h2>🚫 Access Restricted</h2>
                        <p>This section is only available to workspace administrators.</p>
                        <p>Redirecting you to the Tracker...</p>
                    </div>
                </div>
            )
        }

        switch (activeSection) {
            case 'tracker':
                return (
                    <TrackerSection
                        activeSubSection={trackerSubSection}
                        onSubSectionChange={handleTrackerSubSectionChange}
                        apiToken={api_token}
                        user={user}
                        availableProjects={availableProjects}
                        availableTasks={availableTasks}
                        onRefreshNeeded={setWorklogRefreshCallback}
                    />
                )
            case 'calendar':
                return <Calendar apiToken={api_token} user={user} availableProjects={availableProjects} availableTasks={availableTasks} currentWorkspace={current_workspace} />
            case 'reports':
                return (
                    <ReportsSection
                        activeSubSection={reportsSubSection}
                        onSubSectionChange={setReportsSubSection}
                        viewMode={reportsViewMode}
                        onViewModeChange={handleReportsViewModeChange}
                        currentWorkspace={current_workspace}
                    />
                )
            case 'manage':
                return (
                    <ManageSection
                        activeSubSection={manageSubSection}
                        onSubSectionChange={handleManageSubSectionChange}
                        selectedMemberId={selectedMemberId}
                        selectedGroupId={selectedGroupId}
                        selectedProjectId={selectedProjectId}
                        apiToken={api_token}
                        user={user}
                    />
                )
            default:
                return (
                    <div className={styles.content}>
                        <h1>Welcome to {app_name}</h1>
                        <p>Select a section from the menu to get started.</p>
                    </div>
                )
        }
    }

    const handleThemeChange = (newTheme) => {
        setCurrentTheme(newTheme)

        // Save theme preference to localStorage
        if (typeof window !== 'undefined') {
            localStorage.setItem('theme', newTheme)
        }

        // Apply theme to document
        const root = document.documentElement
        root.removeAttribute('data-theme')

        if (newTheme === 'dark') {
            root.setAttribute('data-theme', 'dark')
        } else if (newTheme === 'nightowl') {
            root.setAttribute('data-theme', 'nightowl')
        } else if (newTheme === 'pastel') {
            root.setAttribute('data-theme', 'pastel')
        } else if (newTheme === 'gamer') {
            root.setAttribute('data-theme', 'gamer')
        }
    }

    return (
        <>
            <Head title={`${app_name} - Home`} />
            <div className={styles.adminPage}>
                {/* Header */}
                <header className={`${styles.header} ${current_workspace?.plan && ['pro', 'premium', 'enterprise'].includes(current_workspace.plan) ? styles[current_workspace.plan] : ''}`}>
                    {/* Logo section with staging banner */}
                    <div className={styles.logoSection}>
                        <a href="/" className={styles.logo}>
                            <img src="/logo.png" alt="Kadle Logo" className={styles.logoIcon} />
                            <span className={styles.logoText}>{app_name}</span>
                        </a>
                        <StagingBanner
                            environment={environment}
                            revision={revision}
                            gitlabProjectUrl={gitlab_project_url}
                        />
                    </div>

                    {/* Menu options */}
                    <nav className={styles.nav}>
                        <button
                            className={`${styles.navItem} ${activeSection === 'tracker' ? styles.active : ''}`}
                            onClick={() => handleSectionChange('tracker')}
                        >
                            Tracker
                        </button>
                        <button
                            className={`${styles.navItem} ${activeSection === 'calendar' ? styles.active : ''}`}
                            onClick={() => handleSectionChange('calendar')}
                        >
                            Calendar
                        </button>
                        {user?.is_admin && (
                            <button
                                className={`${styles.navItem} ${activeSection === 'reports' ? styles.active : ''}`}
                                onClick={() => handleSectionChange('reports')}
                            >
                                Reports
                            </button>
                        )}
                        {user?.is_admin && (
                            <button
                                className={`${styles.navItem} ${activeSection === 'manage' ? styles.active : ''}`}
                                onClick={() => handleSectionChange('manage')}
                            >
                                Manage
                            </button>
                        )}
                    </nav>

                    {/* Workspace selector and Avatar on top right */}
                    <div className={styles.rightSection}>
                        {/* Workspace Dropdown */}
                        {available_workspaces && available_workspaces.length > 0 && (
                            <div className={styles.workspaceSection} ref={workspaceMenuRef}>
                                <button
                                    className={styles.workspaceButton}
                                    onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
                                    aria-label="Switch workspace"
                                >
                                    <span className={styles.workspaceName}>
                                        {current_workspace?.name || available_workspaces[0]?.name || 'No Workspace'}
                                    </span>
                                    <span className={styles.dropdownArrow}>▼</span>
                                </button>

                                {/* Workspace dropdown menu */}
                                {showWorkspaceMenu && (
                                    <div className={styles.workspaceMenu}>
                                        <div className={styles.workspaceMenuHeader}>
                                            <span>Switch Workspace</span>
                                        </div>
                                        {available_workspaces.map(workspace => {
                                            const isPremiumTier = workspace.plan && ['pro', 'premium', 'enterprise'].includes(workspace.plan)
                                            const tierClass = isPremiumTier ? workspace.plan : ''
                                            const isCurrentWorkspace = workspace.id === (current_workspace?.id || available_workspaces[0]?.id)

                                            return (
                                                <button
                                                    key={workspace.id}
                                                    className={`${styles.workspaceMenuItem} ${tierClass ? styles[tierClass] : ''} ${isCurrentWorkspace ? styles.currentWorkspace : ''}`}
                                                    onClick={() => handleWorkspaceSwitch(workspace.id)}
                                                    disabled={isCurrentWorkspace}
                                                >
                                                    <span className={styles.workspaceIcon}>🏢</span>
                                                    <span className={styles.workspaceMenuName}>{workspace.name}</span>
                                                    {isPremiumTier && (
                                                        <span className={`${styles.tierBadge} ${styles[workspace.plan]}`}>
                                                            {workspace.plan}
                                                        </span>
                                                    )}
                                                    {isCurrentWorkspace && (
                                                        <span className={styles.currentBadge}>Current</span>
                                                    )}
                                                </button>
                                            )
                                        })}
                                        <hr className={styles.workspaceMenuDivider} />
                                        <button
                                            className={styles.manageWorkspacesButton}
                                            onClick={() => {
                                                setShowWorkspaceMenu(false)
                                                setShowWorkspaceModal(true)
                                            }}
                                        >
                                            <span className={styles.workspaceIcon}>⚙️</span>
                                            <span className={styles.workspaceMenuName}>Manage workspaces</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Avatar */}
                        <div className={styles.userSection} ref={menuRef}>
                            <button
                                className={styles.userAvatar}
                                onClick={toggleUserMenu}
                                aria-label="User menu"
                            >
                                {user?.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt={user.display_name}
                                        className={styles.avatarImage}
                                    />
                                ) : (
                                    <div className={styles.avatarPlaceholder}>
                                        {getInitials(user?.first_name, user?.last_name)}
                                    </div>
                                )}
                            </button>

                            {/* User dropdown menu */}
                            {showUserMenu && (
                                <div className={styles.userMenu}>
                                    <div className={styles.userInfo}>
                                        <div className={styles.userName}>
                                            {user?.first_name} {user?.last_name}
                                        </div>
                                        <div className={styles.userEmail}>
                                            {user?.email}
                                        </div>
                                    </div>
                                    <hr className={styles.menuDivider} />
                                    <button
                                        className={styles.menuItem}
                                        onClick={() => {
                                            setShowUserMenu(false)
                                            setShowPreferences('account')
                                        }}
                                    >
                                        <span className={styles.menuIcon}>👤</span>
                                        Profile Settings
                                    </button>
                                    <button
                                        className={styles.menuItem}
                                        onClick={() => {
                                            setShowUserMenu(false)
                                            setShowPreferences('appearance')
                                        }}
                                    >
                                        <span className={styles.menuIcon}>⚙️</span>
                                        Preferences
                                    </button>
                                    <hr className={styles.menuDivider} />
                                    <button
                                        className={`${styles.menuItem} ${styles.logoutItem}`}
                                        onClick={() => {
                                            setShowUserMenu(false)
                                            handleLogout()
                                        }}
                                    >
                                        <span className={styles.menuIcon}>🚪</span>
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Main content */}
                <main className={styles.main}>
                    {renderContent()}
                </main>
            </div>

            {/* Preferences Modal */}
            {showPreferences && (
                <Preferences
                    onClose={() => setShowPreferences(false)}
                    currentTheme={currentTheme}
                    onThemeChange={handleThemeChange}
                    user={user}
                    workspaces={available_workspaces}
                    currentWorkspace={current_workspace}
                    featureTiers={feature_tiers}
                    initialSection={showPreferences}
                />
            )}

            {/* Workspace Management Modal */}
            <WorkspaceModal
                isOpen={showWorkspaceModal}
                onClose={() => setShowWorkspaceModal(false)}
                workspaces={available_workspaces}
                onWorkspaceCreate={handleWorkspaceCreate}
                onWorkspaceUpdate={handleWorkspaceUpdate}
                onWorkspaceDelete={handleWorkspaceDelete}
            />

            {/* Command K Palette */}
            <CommandPalette
                isOpen={isCommandPaletteOpen}
                onClose={closeCommandPalette}
                apiToken={api_token}
                onSuccess={handleCommandSuccess}
            />

            {/* Timezone Confirmation Prompt */}
            {showTimezonePrompt && detectedTimezone && (
                <TimezonePrompt
                    detectedTimezone={detectedTimezone}
                    currentTimezone={user?.timezone}
                    onUpdate={handleUpdateTimezone}
                    onDismiss={handleDismissTimezone}
                />
            )}

        </>
    )
}
